import React, { useState } from "react";

import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Grid,
  Paper,
  styled,
  Link,
} from "@mui/material";
import ReactPlayer from "react-player";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCutIcon from "@mui/icons-material/ContentCut";

import { Video as VideoType } from "../../types";

interface Props {
  video: VideoType;
  episodeId: string;
}

const VideoCard = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  transition: "all 0.3s ease-in-out",
  cursor: "pointer",
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",

  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[8],
  },

  [theme.breakpoints.down("sm")]: {
    "&:hover": {
      transform: "none",
    },
  },

  "&.generating": {
    cursor: "not-allowed",
    opacity: 0.7,
    "&:hover": {
      transform: "none",
      boxShadow: theme.shadows[2],
    },
  },

  ...theme.applyStyles("dark", {
    backgroundColor: theme.palette.background.paper,
  }),
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "16/9",
  backgroundColor: theme.palette.grey[900],
  overflow: "hidden",
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const OverlayButton = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  color: "white",
  borderRadius: "50%",
  width: 60,
  height: 60,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  zIndex: 2,

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.9)",
    transform: "translate(-50%, -50%) scale(1.1)",
  },

  [theme.breakpoints.down("sm")]: {
    width: 50,
    height: 50,
  },
}));

const GeneratingOverlay = styled(Box)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 3,
  color: "white",
}));

export default function Video({ video, episodeId }: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isGenerating = video.status === "generating" || !video.url;

  const handleMouseEnter = () => {
    if (!isMobile && !isGenerating) {
      setIsHovered(true);
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && !isGenerating) {
      setIsHovered(false);
      setIsPlaying(false);
    }
  };

  const handleTouchStart = () => {
    if (isMobile && !isGenerating) {
      setIsHovered(true);
      setIsPlaying(true);
    }
  };

  const handleTouchEnd = () => {
    if (isMobile && !isGenerating) {
      setTimeout(() => {
        setIsHovered(false);
        setIsPlaying(false);
      }, 2000);
    }
  };

  const renderVideoContent = () => {
    if (isGenerating) {
      return (
        <GeneratingOverlay>
          <Typography
            variant="body2"
            sx={{ textAlign: "center", fontWeight: 500 }}
          >
            Generating Video...
          </Typography>
          <Typography
            variant="caption"
            sx={{ textAlign: "center", opacity: 0.8, mt: 0.5 }}
          >
            This may take a few minutes
          </Typography>
        </GeneratingOverlay>
      );
    }

    return (
      <>
        <ReactPlayer
          url={video.url}
          width="100%"
          height="100%"
          playing={isPlaying}
          muted
          loop
          playsinline
        />

        {/* Play overlay */}
        {!isPlaying && (
          <OverlayButton>
            <PlayArrowIcon sx={{ fontSize: 24 }} />
          </OverlayButton>
        )}

        {/* Edit overlay */}
        {isHovered && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: "white",
                borderRadius: 2,
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 600,
              }}
            >
              <ContentCutIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600}>
                Edit/Export Video
              </Typography>
            </Box>
          </Box>
        )}
      </>
    );
  };

  // If generating, don't wrap in Link
  if (isGenerating) {
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
        {isGenerating ? (
          <VideoCard className="generating">
            <VideoContainer>
              <GeneratingOverlay>
                <Typography
                  variant="body2"
                  sx={{ textAlign: "center", fontWeight: 500 }}
                >
                  Generating Video...
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ textAlign: "center", opacity: 0.8, mt: 0.5 }}
                >
                  This may take a few minutes
                </Typography>
              </GeneratingOverlay>
            </VideoContainer>
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "text.disabled",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 1,
                }}
              >
                {video.title}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label="Short Video"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: 20, sm: 24 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.disabled",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {new Date(video.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </VideoCard>
        ) : (
          <Link
            href={`/episodes/${episodeId}/videos/${video.id}/edit`}
            sx={{ textDecoration: "none", display: "block", height: "100%" }}
          >
            <VideoCard
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <VideoContainer>{renderVideoContent()}</VideoContainer>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  {video.title}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label="Short Video"
                    size="small"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      height: { xs: 20, sm: 24 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    {new Date(video.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </VideoCard>
          </Link>
        )}
      </Grid>
    );
  }

  // If not generating, wrap in Link
  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
      {isGenerating ? (
        <VideoCard className="generating">
          <VideoContainer>
            <GeneratingOverlay>
              <Typography
                variant="body2"
                sx={{ textAlign: "center", fontWeight: 500 }}
              >
                Generating Video...
              </Typography>
              <Typography
                variant="caption"
                sx={{ textAlign: "center", opacity: 0.8, mt: 0.5 }}
              >
                This may take a few minutes
              </Typography>
            </GeneratingOverlay>
          </VideoContainer>
          <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "text.disabled",
                fontSize: { xs: "0.875rem", sm: "1rem" },
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mb: 1,
              }}
            >
              {video.title}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label="Short Video"
                size="small"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  height: { xs: 20, sm: 24 },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                {new Date(video.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </VideoCard>
      ) : (
        <Link
          href={`/episodes/${episodeId}/videos/${video.id}/edit`}
          sx={{ textDecoration: "none", display: "block", height: "100%" }}
        >
          <VideoCard
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <VideoContainer>{renderVideoContent()}</VideoContainer>
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 1,
                }}
              >
                {video.title}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label="Short Video"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    height: { xs: 20, sm: 24 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {new Date(video.created_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </VideoCard>
        </Link>
      )}
    </Grid>
  );
}
